/**
 * Semantic HR & CoWork product media manifest.
 *
 * Media files live on S3: s3://www.myorglab.com/images/semantic-hr/media/
 * Override base: REACT_APP_PRODUCT_MEDIA_URL=https://www.myorglab.com/images/semantic-hr/media
 */

import { assetUrl } from './staticAssets';

export const PRODUCT_MEDIA_BASE = (
  process.env.REACT_APP_PRODUCT_MEDIA_URL || assetUrl('/images/semantic-hr/media')
).replace(/\/$/, '');

/**
 * @param {{ file: string, webm?: string, poster?: string, rev?: string } | string} asset
 * @param {'file'|'webm'|'poster'} [kind]
 */
export function mediaUrl(asset, kind = 'file') {
  const file = typeof asset === 'string'
    ? asset
    : kind === 'webm'
      ? asset.webm
      : kind === 'poster'
        ? asset.poster
        : asset.file;
  if (!file) return null;
  const rev = typeof asset === 'object' && asset.rev ? asset.rev : null;
  const url = `${PRODUCT_MEDIA_BASE}/${file}`;
  return rev ? `${url}?v=${rev}` : url;
}

/**
 * @typedef {Object} ProductMediaAsset
 * @property {string} file
 * @property {string} [webm]
 * @property {string} [poster]
 * @property {string} [rev]
 * @property {number} [width]
 * @property {number} [height]
 * @property {string} alt
 * @property {string} [caption]
 * @property {'semantichr'|'cowork'} surface
 * @property {'png'|'gif'|'mp4'} type
 * @property {'landscape'|'portrait'} orientation
 * @property {string} depicts
 */

const P0_MEDIA_REV = '20260919';

/** Interactive Analytics: diagrams only below CoWork showcase (no section media). */
export const PBI_SECTION_MEDIA = {};

/** Semantic HR page: BI gallery, sprawl & embedded Power BI product media */
export const SEMANTIC_HR_SECTION_MEDIA = {
  'dashboard-sprawl': 'semantichr_clip2',
  'bi-gallery': 'semantichr_split_screen1',
  'embedded-semantic': 'semantichr_split_screen2',
};

/** @type {Record<string, ProductMediaAsset>} */
export const PRODUCT_MEDIA = {
  semantichr_split_screen1: {
    file: 'semantichr_split_screen1.png',
    alt: 'Semantic HR split-screen, copilot chat with headcount trends, embedded Headcount Summary dashboard, and Cortex dynamic visualization',
    caption: 'Headcount, hires, terminations, and open reqs, copilot, Power BI embed, and dynamic chart in one session.',
    surface: 'semantichr',
    type: 'png',
    orientation: 'landscape',
    depicts: 'Full split-screen, 2025 headcount/hires/terminations question with Headcount Summary PBI and Gap to Plan dynamic viz',
  },
  semantichr_split_screen2: {
    file: 'semantichr_split_screen2.png',
    alt: 'Semantic HR split-screen showing voluntary attrition analysis with Terminations dashboard and editable dynamic visualization',
    caption: 'Voluntary attrition by business unit, governed Power BI bookmark plus on-demand chart with edit capability.',
    surface: 'semantichr',
    type: 'png',
    orientation: 'landscape',
    depicts: 'Attrition deep-dive, scatter/bar in chat, Terminations & Attrition PBI, dynamic viz with EDIT VISUALIZATION',
  },
  semantichr_chat_only1: {
    file: 'semantichr_chat_only1.png',
    alt: 'Semantic HR copilot answering headcount by business unit with in-chat bar chart and query results table',
    caption: 'Headcount by business unit, narrative answer, chart, and SQL-backed results in the chat column.',
    surface: 'semantichr',
    type: 'png',
    orientation: 'landscape',
    depicts: 'Chat-only, headcount by BU bar chart + table, Chart/SQL toggles',
  },
  semantichr_chat_only2: {
    file: 'semantichr_chat_only2.png',
    alt: 'Semantic HR copilot comparing termination count vs headcount by business unit with dual in-chat charts',
    caption: 'Termination pressure relative to headcount, dual visualizations from one conversational question.',
    surface: 'semantichr',
    type: 'png',
    orientation: 'landscape',
    depicts: 'Chat-only, termination pressure, bar + grouped bar charts',
  },
  semantichr_clip1: {
    file: 'semantichr_clip1.mp4',
    webm: 'semantichr_clip1.webm',
    poster: 'semantichr_clip1.poster.webp',
    rev: P0_MEDIA_REV,
    width: 1920,
    height: 1080,
    alt: 'Semantic HR Copilot Analyst landing page with suggested headcount, attrition, hiring, and planning questions',
    caption: 'Copilot landing, suggested workforce questions before the first query.',
    surface: 'semantichr',
    type: 'mp4',
    orientation: 'landscape',
    depicts: 'Empty copilot, Copilot/Builder toggle, suggested question cards, chat history sidebar',
  },
  semantichr_clip2: {
    file: 'semantichr_clip2.mp4',
    webm: 'semantichr_clip2.webm',
    poster: 'semantichr_clip2.poster.webp',
    rev: P0_MEDIA_REV,
    width: 1920,
    height: 1080,
    alt: 'Animated Semantic HR session, question to split-screen with hires vs terminations chart, dashboard sync, and dynamic visualization',
    caption: 'Question → governed answer → embedded dashboard and dynamic viz, full workflow.',
    surface: 'semantichr',
    type: 'mp4',
    orientation: 'landscape',
    depicts: 'Animated split-screen workflow, headcount/hires/terminations 2025, dashboard + dynamic net change chart',
  },
  semantichr_main: {
    file: 'semantichr_main.mp4',
    webm: 'semantichr_main.webm',
    poster: 'semantichr_main.poster.webp',
    rev: P0_MEDIA_REV,
    width: 2560,
    height: 1440,
    alt: 'Semantic HR product tour, copilot chat, embedded Power BI, dynamic visualizations, and multi-turn workforce analytics in one session',
    caption: 'Full product walkthrough, conversational analytics, governed BI embeds, and on-demand charts across workforce domains.',
    surface: 'semantichr',
    type: 'mp4',
    orientation: 'landscape',
    depicts: 'Home flagship, multi-beat demo of split-screen app, chat column, PBI gallery, and dynamic viz editing',
  },
  semantichr_clip3: {
    file: 'semantichr_clip3.mp4',
    webm: 'semantichr_clip3.webm',
    poster: 'semantichr_clip3.poster.webp',
    rev: P0_MEDIA_REV,
    width: 1920,
    height: 1080,
    alt: 'Animated voluntary attrition analysis with Power BI Terminations dashboard and Cortex dynamic visualization edit',
    caption: 'Multi-turn attrition analysis, chat charts, PBI embed, and natural-language viz editing.',
    surface: 'semantichr',
    type: 'mp4',
    orientation: 'landscape',
    depicts: 'Animated attrition session, voluntary attrition scatter, Terminations PBI, edit viz prompt',
  },
  cowork_clip1: {
    file: 'cowork_clip1.mp4',
    rev: '20260624',
    alt: 'Snowflake CoWork agent interface ready for a workforce analytics question',
    caption: 'CoWork agent, select agent, extended thinking, and governed Snowflake Cortex responses.',
    surface: 'cowork',
    type: 'mp4',
    orientation: 'portrait',
    depicts: 'CoWork empty state, Select agent, Extended thinking input',
  },
  cowork_clip2: {
    file: 'cowork_clip2.mp4',
    rev: '20260624',
    alt: 'CoWork Semantic HR agent showing monthly net headcount change chart and key observations',
    caption: 'Net headcount change by business unit, chart plus narrative key observations.',
    surface: 'cowork',
    type: 'mp4',
    orientation: 'portrait',
    depicts: 'Net headcount line chart + Key Observations bullets, suggested follow-ups',
  },
  cowork_clip3: {
    file: 'cowork_clip3.mp4',
    rev: '20260624',
    alt: 'CoWork agent chart of monthly hires, terminations, and net change with AI summary',
    caption: 'Hires, terminations, and net change, combo chart with AI key observations.',
    surface: 'cowork',
    type: 'mp4',
    orientation: 'portrait',
    depicts: 'Hires/term/net combo chart + Key Observations (growth vs contraction narrative)',
  },
  cowork_clip4: {
    file: 'cowork_clip4.mp4',
    rev: '20260624',
    alt: 'CoWork agent table of highest termination pressure by business unit with key takeaways',
    caption: 'Termination pressure ranking, table plus five numbered takeaways.',
    surface: 'cowork',
    type: 'mp4',
    orientation: 'portrait',
    depicts: 'Termination pressure table + Key Takeaways (Quality Mgmt, smaller units, net loss universal)',
  },
  cowork_clip5: {
    file: 'cowork_clip5.mp4',
    rev: '20260624',
    alt: 'CoWork agent analysis of voluntary vs involuntary termination mix by business unit',
    caption: 'Voluntary vs involuntary split, concentration by business unit and attrition rates.',
    surface: 'cowork',
    type: 'mp4',
    orientation: 'portrait',
    depicts: 'Text-heavy Key Insights, 74% voluntary, BU concentration, monthly trends',
  },
  cowork_clip6: {
    file: 'cowork_clip6.mp4',
    rev: '20260624',
    alt: 'CoWork agent department-level hiring and backfill pattern analysis',
    caption: 'Department backfill patterns, revolving door vs deliberate shrinkage.',
    surface: 'cowork',
    type: 'mp4',
    orientation: 'portrait',
    depicts: 'Dept table + Patterns to note (Cloud Services churn, backfill rates, zero hiring depts)',
  },
  cowork_clip7: {
    file: 'cowork_clip7.mp4',
    rev: '20260624',
    alt: 'CoWork agent geographic hiring gap analysis by legal entity',
    caption: 'Hiring gaps by country, entity table with trend insight on withdrawn reqs.',
    surface: 'cowork',
    type: 'mp4',
    orientation: 'portrait',
    depicts: 'Geo entity table (US, China, India, Czech) + largest relative gaps + trend insight',
  },
  cowork_clip8: {
    file: 'cowork_clip8.mp4',
    rev: '20260624',
    alt: 'CoWork agent comparing open requisitions to recent terminations by company',
    caption: 'Open reqs vs terminations, pipeline sized beyond backfill needs.',
    surface: 'cowork',
    type: 'mp4',
    orientation: 'portrait',
    depicts: 'Open reqs vs 6-mo terms disconnect by company + BU concentration',
  },
  cowork_clip9: {
    file: 'cowork_clip9.mp4',
    webm: 'cowork_clip9.webm',
    poster: 'cowork_clip9.poster.webp',
    rev: P0_MEDIA_REV,
    width: 2048,
    height: 1152,
    alt: 'CoWork integrated command hub with query history, trending questions, and governed agent answers',
    caption: 'Command hub workspace, query history, trending questions, and governed answers in one view.',
    surface: 'cowork',
    type: 'mp4',
    orientation: 'landscape',
    depicts: 'Snowflake CoWork analytics workspace, integrated command hub',
  },
};

/** Marketing / docs placement groups */
export const MEDIA_PLACEMENTS = {
  hero: ['semantichr_split_screen1', 'semantichr_main'],
  homeFlagship: ['semantichr_split_screen1', 'semantichr_main'],
  splitScreenProof: ['semantichr_clip3', 'semantichr_split_screen2'],
  chatColumn: ['semantichr_chat_only1', 'semantichr_chat_only2'],
  copilotLanding: ['semantichr_clip1'],
  coworkSurface: ['cowork_clip1', 'cowork_clip2', 'cowork_clip3'],
  workforcePlanning: ['cowork_clip7', 'cowork_clip8', 'cowork_clip6', 'semantichr_split_screen1'],
  attrition: ['semantichr_split_screen2', 'cowork_clip4', 'cowork_clip5', 'semantichr_chat_only2'],
  hiringRecruiting: ['cowork_clip6', 'cowork_clip8', 'cowork_clip3'],
  headcount: ['semantichr_chat_only1', 'cowork_clip2', 'cowork_clip3'],
};

export function getMedia(id) {
  return PRODUCT_MEDIA[id] || null;
}
